import { fetchAllArticles } from "@/src/utils/blogArticleList";
import { type Article, getArticleUrl } from "@/src/utils/blogUtils";

import * as Sentry from "@sentry/react";

export interface ArticleRouteData {
  filenames: string[];
  filenameToSlug: Record<string, string>;
  articles: Record<string, Article>;
}

const EMPTY: ArticleRouteData = {
  filenames: [],
  filenameToSlug: {},
  articles: {}
};

// Prerender (Node) has no `window`; the browser does. Used to pick between the
// build-time memoized/fail-loud path and the runtime graceful-degradation path.
const IS_BUILD = typeof window === "undefined";

// One in-flight fetch pass reused across a single build. See `loadArticleRouteData`.
let buildDataPromise: Promise<ArticleRouteData> | undefined;

// Resolves the article set from the bucket. Throws on a fetch failure (the
// manifest fetch rejects on a non-OK response) so callers can decide how to
// handle it. Returns EMPTY only when the source is not configured at all.
const resolveArticleRouteData = async (): Promise<ArticleRouteData> => {
  const bucketUrl = import.meta.env.VITE_FRONTPAGE_BUCKET_URL;
  const articlesPath = import.meta.env.VITE_FRONTPAGE_ARTICLES_RELATIVE_PATH;
  const listFilename = import.meta.env.VITE_FRONTPAGE_ARTICLE_LIST_FILENAME;

  if (!bucketUrl || !articlesPath || !listFilename) {
    return EMPTY;
  }

  const allArticles = await fetchAllArticles<Article>(
    bucketUrl,
    articlesPath,
    listFilename,
    { strict: false }
  );

  const filenames: string[] = [];
  const filenameToSlug: Record<string, string> = {};
  const articles: Record<string, Article> = {};

  for (const { filename, article } of allArticles) {
    const slug = getArticleUrl(article.title);
    filenames.push(filename);
    filenameToSlug[filename] = slug;
    articles[slug] = article;
  }

  return { filenames, filenameToSlug, articles };
};

// Shared by the blog routes for both the build-time `loader` (prerender + SEO
// meta) and the runtime `clientLoader`. In SPA mode (ssr:false) the prerendered
// single-fetch data fails to hydrate for routes that export a loader, so the
// client must re-resolve this data itself.
//
// At build time every prerendered route (`/blog` plus each `/blog/<slug>`)
// calls this. Without sharing, each would re-download the entire article set,
// flooding the bucket with hundreds of requests; a single transient failure
// then silently degrades that page to an empty skeleton (wrecking SEO). So the
// build path memoizes one in-flight promise for the whole build. Unreachable
// individual articles are skipped so one bad S3 object cannot block the whole
// build; an empty result still fails via build:verify and prerender validation.
export const loadArticleRouteData = async (): Promise<ArticleRouteData> => {
  if (IS_BUILD) {
    buildDataPromise ??= resolveArticleRouteData();
    try {
      return await buildDataPromise;
    } catch (error) {
      // Reset so a retried build (or a later loader) can attempt the fetch again.
      buildDataPromise = undefined;
      throw error;
    }
  }

  try {
    return await resolveArticleRouteData();
  } catch (error) {
    Sentry.captureException(error);
    return EMPTY;
  }
};
